import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  Cpu,
  ExternalLink,
  CheckCircle2,
  Circle,
  Loader2,
  Smartphone,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

export default function NfcProgrammingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const { data } = await api.get(`/customers/${id}`);
      setCustomer(data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/kunden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setStep = async (key, value) => {
    try {
      const { data } = await api.patch(`/customers/${id}/activation`, { key, value });
      setCustomer(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(customer.nfc_link);
      toast.success("NFC-Link kopiert.");
      if (!customer.activation_checklist?.nfc_copied) {
        await setStep("nfc_copied", true);
      }
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const testNfc = () => {
    window.open(customer.nfc_link, "_blank", "noopener");
    if (!customer.activation_checklist?.nfc_tested) {
      setStep("nfc_tested", true);
    }
  };

  if (loading || !customer) {
    return (
      <div className="text-muted-foreground text-sm flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
      </div>
    );
  }

  const cl = customer.activation_checklist || {};
  const allDone = cl.nfc_copied && cl.nfc_tested;

  const steps = [
    {
      key: "nfc_copied",
      title: "NFC-Link kopieren",
      body: "Kopiere die Aufsteller-URL in die Zwischenablage.",
      cta: (
        <Button
          onClick={copyLink}
          className="rounded-full gap-2"
          data-testid="nfc-step1-copy-btn"
        >
          <Copy className="w-4 h-4" /> Link kopieren
        </Button>
      ),
    },
    {
      key: null,
      title: "NFC-Chip programmieren",
      body: 'Öffne die App „NFC Tools" auf dem Handy → Schreiben → Datensatz hinzufügen → URL/URI → eingefügten Link einfügen → auf den Chip schreiben.',
      cta: (
        <Button
          variant="secondary"
          className="rounded-full gap-2"
          onClick={() => window.open("https://play.google.com/store/apps/details?id=com.wakdev.wdnfc", "_blank", "noopener")}
          data-testid="nfc-step2-app-btn"
        >
          <Smartphone className="w-4 h-4" /> &bdquo;NFC Tools&ldquo; &ouml;ffnen
        </Button>
      ),
    },
    {
      key: "nfc_tested",
      title: "NFC-Chip testen",
      body: "Halte das Handy an den Chip. Der Link muss sich öffnen und beim Google-Bewertungsdialog landen.",
      cta: (
        <Button
          onClick={testNfc}
          variant="secondary"
          className="rounded-full gap-2"
          data-testid="nfc-step3-test-btn"
        >
          <ExternalLink className="w-4 h-4" /> Aufsteller-Link öffnen
        </Button>
      ),
    },
    {
      key: "confirm",
      title: "Als erledigt markieren",
      body: "Wenn Programmierung und Test funktionieren, hier bestätigen.",
      cta: (
        <Button
          onClick={async () => {
            if (!cl.nfc_copied) await setStep("nfc_copied", true);
            if (!cl.nfc_tested) await setStep("nfc_tested", true);
            toast.success("NFC als fertig markiert.");
          }}
          disabled={allDone}
          className="rounded-full gap-2"
          data-testid="nfc-step4-confirm-btn"
        >
          {allDone ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
          {allDone ? "Erledigt" : "NFC ist fertig"}
        </Button>
      ),
    },
  ];

  const isDone = (key) => (key === "confirm" ? allDone : !!cl[key]);

  return (
    <div className="space-y-6" data-testid="nfc-programming-page">
      <div className="flex items-center justify-between gap-3">
        <Link
          to={`/kunden/${customer.id}`}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"
          data-testid="back-to-customer"
        >
          <ArrowLeft className="w-3 h-3" /> Zurück zum Kunden
        </Link>
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Cpu className="w-3 h-3" /> NFC-Programmierung
        </span>
      </div>

      <div>
        <p className="label-eyebrow">NFC-Chip programmieren</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 break-words">
          {customer.business_name}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          In vier kurzen Schritten ist der NFC-Chip aktiviert.
        </p>
      </div>

      <div className="card-surface p-4" data-testid="nfc-link-panel">
        <div className="label-eyebrow mb-1">Aufsteller-Link (identisch zum QR)</div>
        <div className="font-mono text-xs bg-white/[0.02] px-3 py-2 rounded border border-white/[0.05] break-all">
          {customer.nfc_link}
        </div>
      </div>

      <ol className="space-y-3" data-testid="nfc-steps-list">
        {steps.map((step, i) => {
          const done = isDone(step.key);
          return (
            <li
              key={i}
              className={`card-surface p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${
                done ? "border-emerald-500/20" : ""
              }`}
              data-testid={`nfc-step-${i + 1}`}
            >
              <div className="flex items-center gap-3 min-w-0 sm:flex-1">
                <div
                  className={`w-9 h-9 rounded-full grid place-items-center font-semibold shrink-0 ${
                    done
                      ? "bg-emerald-500/15 text-emerald-400"
                      : "bg-primary/10 text-primary"
                  }`}
                >
                  {done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{step.title}</div>
                  <p className="text-xs text-muted-foreground mt-0.5">{step.body}</p>
                </div>
              </div>
              <div className="sm:shrink-0">{step.cta}</div>
            </li>
          );
        })}
      </ol>

      <Separator className="bg-white/[0.06]" />

      <div className="text-xs text-muted-foreground flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
        Chip erst dann versiegeln, wenn Schritt 3 (Testen) erfolgreich war.
        Änderungen sind danach nur mit einem neuen Chip möglich.
      </div>
    </div>
  );
}
